import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import InputMoney from './InputMoney';
import Select from './Select';
import Spinner from './Spinner';
import { useFirebase } from './FirebaseContextProvider';
import { useModal } from './ModalProvider';
import { paymentCreateManual } from '../utils/firebase';
import { useTeamsDirectory } from '../utils/hooks';
import { PaymentPurposes } from '../utils/enums';
import './ManualPay.scss';

export default function ManualPay({ method }) {
  const [spinner, setSpinner] = useState(false);
  const { tournaments } = useFirebase();
  // Two pickers over the organization — the directory is what it is for
  const { teams } = useTeamsDirectory();
  const { closeModal } = useModal();

  const teamOptions = useMemo(
    () =>
      teams
        .map((t) => t.name)
        .sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' })),
    [teams]
  );
  const tournamentOptions = useMemo(
    () =>
      tournaments
        .map((t) => t.name)
        .sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' })),
    [tournaments]
  );

  const initialValues = { team: '', tournament: '', amount: '' };
  const validationSchema = Yup.object({
    team: Yup.string().required('Required'),
    tournament: Yup.string().required('Required'),
    amount: Yup.number()
      .typeError('Must be a number')
      .positive('Must be positive')
      .required('Required'),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setSpinner(true);
      try {
        await paymentCreateManual({
          details: {
            team: teams.find((t) => t.name === values.team)?.id,
            tournament: tournaments.find((t) => t.name === values.tournament)
              ?.id,
          },
          amount: +values.amount,
          method,
          purpose: PaymentPurposes.TOURNAMENT,
        });
        formik.resetForm();
        closeModal();
      } catch (error) {
        toast.error(error?.message ?? 'Failed to add payment');
      } finally {
        setSpinner(false);
      }
    },
  });

  return (
    <form className='cash-pay' onSubmit={formik.handleSubmit}>
      <p className='font-16'>Add {method} payment</p>

      <Select
        options={teamOptions}
        search
        value={formik.values.team}
        onChange={(val) => formik.setFieldValue('team', val)}
        placeholder='Select team'
        error={formik.touched.team && formik.errors.team}
      />

      <Select
        options={tournamentOptions}
        search
        value={formik.values.tournament}
        onChange={(val) => formik.setFieldValue('tournament', val)}
        placeholder='Select tournament'
        error={formik.touched.tournament && formik.errors.tournament}
      />

      <InputMoney
        name='amount'
        value={formik.values.amount}
        onChange={(value) => formik.setFieldValue('amount', value)}
        onBlur={formik.handleBlur}
        error={
          formik.touched.amount && formik.errors.amount
            ? formik.errors.amount
            : null
        }
      />

      <div className='btns-wrap'>
        <button
          type='button'
          className='bw'
          onClick={() => {
            closeModal();
            formik.resetForm();
          }}
        >
          Cancel
        </button>
        <button type='submit'>Add cash</button>
      </div>

      <Spinner show={spinner} />
    </form>
  );
}
